'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { useCallback } from 'react'
import { Maximize2, Contrast } from 'lucide-react'
import { useTheme } from 'next-themes'
import type { SkriptFilesData } from '@/lib/skript-files'
import { resolveFile, resolveUrl } from '@/lib/skript-files'
import { decodeFileRef, isLocalFileRef } from '@/lib/file-refs'
import { ResizableWrapper } from './resizable-wrapper'
import { ImageLightbox } from './image-lightbox'

/**
 * Parse markdown links [text](url) in a string and return React elements
 */
function parseMarkdownLinks(text: string): React.ReactNode {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    // Add the link
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:underline not-italic"
      >
        {match[1]}
      </a>
    )
    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? parts : text
}

interface ContentImageProps {
  src: string // Filename (e.g., "image.png")
  alt?: string
  title?: string
  style?: React.CSSProperties
  onWidthChange?: (markdown: string) => void
  originalSrc?: string // The original filename from markdown (before URL resolution)
  align?: 'left' | 'center' | 'right'
  wrap?: boolean
  inline?: boolean // Render as an inline-block <img> (e.g. a logo inside a heading), no block wrapper/handles
  invert?: 'dark' | 'light' | 'always' // Invert colors for diagrams
  saturate?: string // Saturation percentage to apply with invert (e.g., '70' or '150')
  optimizeImages?: boolean // Enable Next.js Image optimization (WebP/AVIF, resizing)
  nozoom?: boolean // Suppress the fullscreen/zoom button and lightbox (e.g. UI screenshots linked to another page)
  // Files data for resolving URLs (serializable)
  files?: SkriptFilesData
  // Source line tracking for editor sync
  sourceLineStart?: string
  sourceLineEnd?: string
}

export function ContentImage({ src, alt = '', title, style, onWidthChange, originalSrc, align = 'center', wrap = false, inline = false, invert, saturate, optimizeImages, nozoom = false, files, sourceLineStart, sourceLineEnd }: ContentImageProps) {
  const filename = originalSrc || src
  const { resolvedTheme: themeResolved, forcedTheme } = useTheme()
  const resolvedTheme = forcedTheme ?? themeResolved
  const [lightboxOpen, setLightboxOpen] = useState(false)

  // Resolve the image URL and dimensions
  // `filename`, not `src`: remark-rehype percent-encodes src, and data-original-src
  // holds the reference as written in the markdown.
  const isRelativePath = filename && isLocalFileRef(filename)
  const fileInfo = isRelativePath && files ? resolveFile(files, filename) : undefined
  const resolvedSrc = fileInfo?.url
  const imageSrc = resolvedSrc ?? src
  const isMissing = isRelativePath && !resolvedSrc

  // Use stored dimensions if available, fall back to defaults
  const imgWidth = fileInfo?.width ?? 800
  const imgHeight = fileInfo?.height ?? 600

  // Local invert state so the editor's invert gizmo previews instantly; the
  // prop is the saved value from the markdown round-trip.
  const [currentInvert, setCurrentInvert] = useState(invert)
  useEffect(() => {
    setCurrentInvert(invert)
  }, [invert])

  // Calculate if we should apply invert filter
  const shouldInvert = currentInvert === 'always' ||
    (currentInvert === 'dark' && resolvedTheme === 'dark') ||
    (currentInvert === 'light' && resolvedTheme === 'light')

  // Build the filter string with optional saturate
  const invertFilter = shouldInvert
    ? `invert(1) hue-rotate(180deg)${saturate ? ` saturate(${saturate})` : ''}`
    : undefined

  // Parse initial width from style
  const initialWidth = style?.width && typeof style.width === 'string' && style.width.includes('%')
    ? parseFloat(style.width)
    : 100

  // Rebuild the <img> tag from current attributes, with overrides for
  // whichever gizmo just changed (layout from the wrapper, invert from the
  // invert button). Uses style for width, data-* for custom attributes.
  const buildImgTag = useCallback((overrides: {
    width?: number
    align?: 'left' | 'center' | 'right'
    wrap?: boolean
    invert?: 'dark' | 'light' | 'always' | undefined
  }) => {
    const nextAlign = overrides.align ?? align
    const nextWrap = overrides.wrap ?? wrap
    const nextInvert = 'invert' in overrides ? overrides.invert : currentInvert
    let attrs = `src="${filename}" alt="${alt}" style="width: ${Math.round(overrides.width ?? initialWidth)}%"`
    if (nextAlign !== 'center') {
      attrs += ` data-align="${nextAlign}"`
    }
    if (nextWrap) {
      attrs += ` data-wrap="true"`
    }
    if (nextInvert) {
      attrs += ` data-invert="${nextInvert}"`
    }
    if (saturate) {
      attrs += ` data-saturate="${saturate}"`
    }
    if (nozoom) {
      attrs += ` data-nozoom="true"`
    }
    return `<img ${attrs} />`
  }, [alt, filename, align, wrap, currentInvert, initialWidth, saturate, nozoom])

  // Handle layout changes from the wrapper
  const handleLayoutChange = useCallback((layout: { width: number; align: 'left' | 'center' | 'right'; wrap: boolean }) => {
    if (!onWidthChange) return
    onWidthChange(buildImgTag(layout))
  }, [onWidthChange, buildImgTag])

  // Cycle invert mode: off → dark → light → always → off
  const handleInvertCycle = useCallback(() => {
    const order: Array<'dark' | 'light' | 'always' | undefined> = [undefined, 'dark', 'light', 'always']
    const next = order[(order.indexOf(currentInvert) + 1) % order.length]
    setCurrentInvert(next)
    onWidthChange?.(buildImgTag({ invert: next }))
  }, [currentInvert, onWidthChange, buildImgTag])

  // Inline logo/icon mode: an inline-block logo that flows with surrounding text
  // (e.g. inside an <h1>). No resize/align handles, no float wrapper. Sizes to the
  // text (height:1em) by default; author style (width/height) overrides, and the
  // unset dimension stays auto so aspect ratio is preserved.
  // Placed after all hooks so it doesn't create a conditional-hook path.
  //
  // Wrapped in a span that exposes the resolved URL as `--logo-url`. Inside a
  // color-title <h1>, the rainbow shadow layer masks that URL over the animated
  // gradient to paint a rainbow silhouette of the logo (globals.css) — plain
  // background-clip can't colour a replaced <img>, so we fill its alpha shape.
  if (inline) {
    const imgStyle: React.CSSProperties = {
      display: 'block',
      width: 'auto',
      height: 'auto',
      ...(invertFilter ? { filter: invertFilter } : {}),
      ...style,
    }
    if (!style?.width && !style?.height) imgStyle.height = '1em'
    return (
      <span
        className="ez-inline-logo"
        style={{
          display: 'inline-block',
          verticalAlign: '-0.15em',
          position: 'relative',
          ['--logo-url' as string]: `url("${imageSrc}")`,
        }}
      >
        {/* Plain img (not next/image): inline flow + intrinsic sizing, no block wrapper. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={imageSrc} alt={alt || ''} title={title} style={imgStyle} loading="lazy" />
      </span>
    )
  }

  // Build data attributes for source line tracking
  const dataAttributes: Record<string, string> = {}
  if (sourceLineStart) dataAttributes['source-line-start'] = sourceLineStart
  if (sourceLineEnd) dataAttributes['source-line-end'] = sourceLineEnd

  return (
    <ResizableWrapper
      initialWidth={initialWidth}
      align={align}
      wrap={wrap}
      onLayoutChange={onWidthChange ? handleLayoutChange : undefined}
      dataAttributes={dataAttributes}
      naturalMaxWidth={imgWidth}
      extraControls={
        // Invert gizmo — cycles off → dark → light → always and persists as
        // data-invert on the <img> tag. Lives in the wrapper's control group.
        !isMissing ? (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleInvertCycle()
            }}
            className={`p-1.5 rounded hover:bg-accent transition-colors ${
              currentInvert ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'
            }`}
            title={
              currentInvert === 'dark' ? 'Inverting in dark mode — click for: invert in light mode'
              : currentInvert === 'light' ? 'Inverting in light mode — click for: always invert'
              : currentInvert === 'always' ? 'Always inverting — click to turn invert off'
              : 'Invert image colors (for dark mode diagrams)'
            }
          >
            <Contrast className="w-3.5 h-3.5" />
          </button>
        ) : undefined
      }
    >
      {/* Image */}
      <span className="block relative group/img">
        {/* Fullscreen — published view only; useless in the editor preview and
            it collided with the wrapper's control group. */}
        {!isMissing && !nozoom && !onWidthChange && (
          <button
            onClick={() => setLightboxOpen(true)}
            className="absolute top-2 right-2 z-20 p-1.5 rounded-md bg-background/80 backdrop-blur-xs border border-border shadow-xs opacity-0 group-hover/img:opacity-100 transition-opacity hover:bg-accent cursor-zoom-in"
            title="Fullscreen"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        )}
        {isMissing ? (
          <span className="flex items-center justify-center w-full h-32 bg-muted border border-dashed border-border rounded-md text-muted-foreground text-sm">
            Missing: {decodeFileRef(filename)}
          </span>
        ) : imageSrc.startsWith('http') && !optimizeImages ? (
          // External URLs without optimization: use native img
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={imageSrc}
            alt={alt || ''}
            title={title}
            className="w-full h-auto rounded-md"
            style={invertFilter ? { filter: invertFilter } : undefined}
            loading="lazy"
          />
        ) : (
          <Image
            src={imageSrc}
            alt={alt || ''}
            title={title}
            width={imgWidth}
            height={imgHeight}
            className="w-full h-auto rounded-md"
            style={invertFilter ? { filter: invertFilter } : undefined}
            unoptimized={!optimizeImages}
          />
        )}
      </span>

      {/* Caption - supports markdown links [text](url) */}
      {alt && (
        <span className="block mt-2 text-sm text-center text-muted-foreground italic">
          {parseMarkdownLinks(alt)}
        </span>
      )}

      {/* Lightbox */}
      {!isMissing && !nozoom && (
        <ImageLightbox open={lightboxOpen} onClose={() => setLightboxOpen(false)}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageSrc}
            alt={alt || ''}
            className="max-w-[95vw] max-h-[90vh] w-auto h-auto object-contain rounded-md"
            style={{
              width: '95vw',
              height: '90vh',
              ...(invertFilter ? { filter: invertFilter } : {}),
            }}
          />
        </ImageLightbox>
      )}
    </ResizableWrapper>
  )
}
